// Log levels
export const LogLevel = {
    DEBUG: 'DEBUG',
    INFO: 'INFO',
    WARN: 'WARN',
    ERROR: 'ERROR',
    CRITICAL: 'CRITICAL'
};

// Log categories
export const LogCategory = {
    AUTH: 'AUTH',
    SESSION: 'SESSION',
    SECURITY: 'SECURITY',
    NETWORK: 'NETWORK',
    VALIDATION: 'VALIDATION',
    SYSTEM: 'SYSTEM',
    USER: 'USER'
};

// Logger class
export class Logger {
    constructor() {
        this.logHistory = [];
        this.maxLogHistory = 1000;
        this.logLevel = process.env.NODE_ENV === 'production' ? LogLevel.INFO : LogLevel.DEBUG;
        this.enableConsole = true;
        this.enableFileLogging = false; // Would be enabled in production
    }

    // Set log level
    setLogLevel(level) {
        this.logLevel = level;
    }

    // Check if should log based on level
    shouldLog(level) {
        const levels = Object.values(LogLevel);
        const currentIndex = levels.indexOf(this.logLevel);
        const messageIndex = levels.indexOf(level);
        return messageIndex >= currentIndex;
    }

    // Create log entry
    createLogEntry(level, category, message, data = {}) {
        return {
            timestamp: new Date().toISOString(),
            level,
            category,
            message,
            data,
            userAgent: navigator.userAgent,
            url: window.location.href,
            sessionId: this.getSessionId()
        };
    }

    // Get session ID for tracking
    getSessionId() {
        return localStorage.getItem('session_id') || 'anonymous';
    }

    // Log message
    log(level, category, message, data = {}) {
        if (!this.shouldLog(level)) return;

        const logEntry = this.createLogEntry(level, category, message, data);
        
        // Add to history
        this.logHistory.push(logEntry);
        if (this.logHistory.length > this.maxLogHistory) {
            this.logHistory.shift();
        }

        // Console logging
        if (this.enableConsole) {
            this.consoleLog(level, logEntry);
        }

        // File logging (for production)
        if (this.enableFileLogging) {
            this.fileLog(logEntry);
        }

        // Send to analytics in production
        if (process.env.NODE_ENV === 'production' && level === LogLevel.ERROR) {
            this.sendToAnalytics(logEntry);
        }
    }

    // Console logging with colors
    consoleLog(level, logEntry) {
        const colors = {
            [LogLevel.DEBUG]: '#6B7280',
            [LogLevel.INFO]: '#3B82F6',
            [LogLevel.WARN]: '#F59E0B',
            [LogLevel.ERROR]: '#EF4444',
            [LogLevel.CRITICAL]: '#DC2626'
        };

        const color = colors[level] || '#6B7280';
        const prefix = `%c[${level}]%c [${logEntry.category}]`;
        const style = `color: ${color}; font-weight: bold;`;
        const resetStyle = 'color: inherit; font-weight: normal;';

        console.log(prefix, style, resetStyle, logEntry.message, logEntry.data);
    }

    // File logging (placeholder for production)
    fileLog(logEntry) {
        // In production, this would write to a log file
        // For now, we'll just store in memory
        console.log('File Log:', JSON.stringify(logEntry));
    }

    // Send to analytics (placeholder)
    sendToAnalytics(logEntry) {
        // In production, this would send to analytics service
        // Example: analytics.track('app_log', logEntry);
        console.log('Analytics Log:', logEntry);
    }

    // Convenience methods
    debug(category, message, data = {}) {
        this.log(LogLevel.DEBUG, category, message, data);
    }

    info(category, message, data = {}) {
        this.log(LogLevel.INFO, category, message, data);
    }

    warn(category, message, data = {}) {
        this.log(LogLevel.WARN, category, message, data);
    }

    error(category, message, data = {}) {
        this.log(LogLevel.ERROR, category, message, data);
    }

    critical(category, message, data = {}) {
        this.log(LogLevel.CRITICAL, category, message, data);
    }

    // Auth-specific logging
    authInfo(message, data = {}) {
        this.info(LogCategory.AUTH, message, data);
    }

    authError(message, data = {}) {
        this.error(LogCategory.AUTH, message, data);
    }

    sessionInfo(message, data = {}) {
        this.info(LogCategory.SESSION, message, data);
    }

    sessionError(message, data = {}) {
        this.error(LogCategory.SESSION, message, data);
    }

    securityInfo(message, data = {}) {
        this.info(LogCategory.SECURITY, message, data);
    }

    securityError(message, data = {}) {
        this.error(LogCategory.SECURITY, message, data);
    }

    // Get log statistics
    getLogStats() {
        const stats = {
            total: this.logHistory.length,
            byLevel: {},
            byCategory: {},
            recentErrors: this.logHistory
                .filter(log => log.level === LogLevel.ERROR || log.level === LogLevel.CRITICAL)
                .slice(-10)
        };

        this.logHistory.forEach(log => {
            stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1;
            stats.byCategory[log.category] = (stats.byCategory[log.category] || 0) + 1;
        });

        return stats;
    }

    // Clear log history
    clearLogHistory() {
        this.logHistory = [];
    }

    // Export logs (for debugging)
    exportLogs() {
        return JSON.stringify(this.logHistory, null, 2);
    }
}

// Global logger instance
export const logger = new Logger();
